import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import {
  BookOpen,
  FileText,
  Download,
  ExternalLink,
  HelpCircle,
  MapPin,
  Building,
  Users,
  CheckCircle,
  IndianRupee,
  Landmark,
  ArrowRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Map benefit icons stored as strings to actual lucide components.
const BENEFIT_ICONS = {
  Building,
  IndianRupee,
  Landmark,
  Users,
  MapPin,
  CheckCircle,
  FileText,
};

const KnowledgeHubPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/content/knowledge-articles`)
      .then((res) => {
        if (cancelled) return;
        const active = (res.data || []).filter((a) => a.is_active !== false);
        setArticles(active);
      })
      .catch(() => {
        if (!cancelled) setArticles([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const grouped = useMemo(() => {
    const g = {
      notification: [],
      process: [],
      benefit: [],
      faq: [],
      resource: [],
    };
    for (const a of articles) {
      if (g[a.type]) g[a.type].push(a);
    }
    // Sort each group by order
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return g;
  }, [articles]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="knowledge-hub-page">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#4E80FF] to-[#2952CC] py-12">
        <div className="container-main">
          <div className="flex items-center gap-2 text-white/80 mb-4">
            <BookOpen className="w-5 h-5" />
            <span className="text-sm font-medium">
              Delhi &middot; DDA Land Pooling Policy 2018
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            Knowledge Hub
          </h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Everything you need to understand and act on Delhi&apos;s Land
            Pooling Policy &mdash; official notifications, the 7-stage
            development procedure, key benefits, FAQs and downloadable
            resources &mdash; all sourced from DDA.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-10">
        <div className="container-main">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 max-w-4xl mx-auto mb-8">
              <TabsTrigger value="overview" data-testid="tab-overview">
                Overview
              </TabsTrigger>
              <TabsTrigger value="notifications" data-testid="tab-notifications">
                Notifications
              </TabsTrigger>
              <TabsTrigger value="process" data-testid="tab-process">
                Process
              </TabsTrigger>
              <TabsTrigger value="faqs" data-testid="tab-faqs">
                FAQs
              </TabsTrigger>
              <TabsTrigger value="resources" data-testid="tab-resources">
                Resources
              </TabsTrigger>
            </TabsList>

            {/* Overview */}
            <TabsContent value="overview" className="space-y-8">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-8">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">
                    What is Delhi&apos;s Land Pooling Policy?
                  </h2>
                  <p className="text-slate-700 leading-relaxed">
                    Under DDA&apos;s Land Pooling Policy notified on 11 October
                    2018, landowners voluntarily pool their contiguous parcels
                    within notified zones (J, K-I, L, N, P-I, P-II). DDA
                    develops city infrastructure on the pooled land and returns{" "}
                    <strong>60% of the developed land</strong> to the consortium
                    for residential/commercial use, while retaining{" "}
                    <strong>40%</strong> for services and city-level
                    infrastructure.
                  </p>
                </CardContent>
              </Card>

              {/* Benefits */}
              {grouped.benefit.length > 0 && (
                <div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-4">
                    Key Benefits
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {grouped.benefit.map((b) => {
                      const Icon = BENEFIT_ICONS[b.icon] || CheckCircle;
                      return (
                        <Card
                          key={b.id}
                          className="border-0 shadow-sm card-hover"
                          data-testid={`benefit-${b.id}`}
                        >
                          <CardContent className="p-6">
                            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-3">
                              <Icon className="w-6 h-6 text-[#4E80FF]" />
                            </div>
                            <h4 className="font-semibold text-slate-900 mb-1">
                              {b.title}
                            </h4>
                            <p className="text-sm text-slate-600">
                              {b.description}
                            </p>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quick stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-0 shadow-sm text-center">
                  <CardContent className="p-6">
                    <p className="text-3xl font-bold text-[#4E80FF]">6</p>
                    <p className="text-sm text-slate-600">Notified Zones</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm text-center">
                  <CardContent className="p-6">
                    <p className="text-3xl font-bold text-[#4E80FF]">95</p>
                    <p className="text-sm text-slate-600">Villages</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm text-center">
                  <CardContent className="p-6">
                    <p className="text-3xl font-bold text-[#4E80FF]">60:40</p>
                    <p className="text-sm text-slate-600">Land Return Ratio</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm text-center">
                  <CardContent className="p-6">
                    <p className="text-3xl font-bold text-[#4E80FF]">7</p>
                    <p className="text-sm text-slate-600">Process Stages</p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Notifications */}
            <TabsContent value="notifications" className="space-y-3">
              {grouped.notification.length === 0 ? (
                <p className="text-center text-slate-500 py-8">
                  No notifications yet.
                </p>
              ) : (
                grouped.notification.map((n) => (
                  <a
                    key={n.id}
                    href={n.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    data-testid={`notification-${n.id}`}
                  >
                    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                      <CardContent className="p-5 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="w-11 h-11 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                            <FileText className="w-5 h-5 text-[#4E80FF]" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 truncate">
                              {n.title}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              {n.category && (
                                <Badge variant="secondary" className="text-xs">
                                  {n.category}
                                </Badge>
                              )}
                              {n.date && (
                                <span className="text-xs text-slate-500">
                                  {n.date}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <ExternalLink className="w-5 h-5 text-slate-400 shrink-0" />
                      </CardContent>
                    </Card>
                  </a>
                ))
              )}
            </TabsContent>

            {/* Process */}
            <TabsContent value="process" className="space-y-4">
              {grouped.process.length === 0 ? (
                <p className="text-center text-slate-500 py-8">
                  No process steps yet.
                </p>
              ) : (
                grouped.process.map((p) => (
                  <Card
                    key={p.id}
                    className="border-0 shadow-sm"
                    data-testid={`process-step-${p.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex gap-4">
                        <div className="w-11 h-11 rounded-full bg-[#4E80FF] text-white flex items-center justify-center font-bold shrink-0">
                          {p.stage || p.order}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-heading text-lg font-bold text-slate-900">
                            {p.title}
                          </h4>
                          <p className="text-slate-600 mt-1">{p.description}</p>
                          {Array.isArray(p.details) && p.details.length > 0 && (
                            <ul className="mt-3 space-y-1.5">
                              {p.details.map((d, idx) => (
                                <li
                                  key={idx}
                                  className="flex items-start gap-2 text-sm text-slate-700"
                                >
                                  <ArrowRight className="w-4 h-4 text-[#4E80FF] shrink-0 mt-0.5" />
                                  <span>{d}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            {/* FAQs */}
            <TabsContent value="faqs">
              {grouped.faq.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No FAQs yet.</p>
              ) : (
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-6">
                    <Accordion type="single" collapsible className="w-full">
                      {grouped.faq.map((f) => (
                        <AccordionItem
                          key={f.id}
                          value={f.id}
                          data-testid={`faq-${f.id}`}
                        >
                          <AccordionTrigger className="text-left">
                            <span className="flex items-center gap-2">
                              <HelpCircle className="w-4 h-4 text-[#4E80FF] shrink-0" />
                              {f.question}
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="text-slate-700 leading-relaxed pl-6">
                            {f.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Resources */}
            <TabsContent value="resources" className="grid md:grid-cols-2 gap-4">
              {grouped.resource.length === 0 ? (
                <p className="text-center text-slate-500 py-8 col-span-2">
                  No resources yet.
                </p>
              ) : (
                grouped.resource.map((r) => (
                  <a
                    key={r.id}
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    data-testid={`resource-${r.id}`}
                  >
                    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow h-full">
                      <CardContent className="p-6 flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                          <FileText className="w-6 h-6 text-[#4E80FF]" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">
                            {r.title}
                          </p>
                          <p className="text-sm text-slate-600 mt-1">
                            {r.description}
                          </p>
                          <div className="flex items-center gap-1 mt-2 text-xs text-[#4E80FF] font-medium">
                            <Download className="w-3 h-3" />
                            Download PDF
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                ))
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-slate-900">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">
            Ready to Pool Your Land?
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Apply through DDA&apos;s official Single Window portal, or explore
            our services to form a consortium.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href="https://online.dda.org.in/landpooling/AppForm/default.aspx"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="bg-[#4E80FF] hover:bg-[#2952CC]">
                <ExternalLink className="w-5 h-5 mr-2" />
                Apply on DDA Portal
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KnowledgeHubPage;
